/*
 * Gourmet Easy PDP reviews. Rebuilds Judge.me's hidden per-product widget
 * into our own summary + card grid; Judge.me's own CSS/JS fights a restyle
 * in place. See ge-product-reviews.liquid for the hidden source.
 */
(() => {
  // Four cards a page, matching the design. The inline widget only carries
  // Judge.me's first page (5), so everything past that is fetched.
  const PER_PAGE = 4;
  const FETCH_PER_PAGE = 30;

  /*
   * Two different defects used to share one test, and only one of them was
   * real.
   *
   * The real one: Judge.me's cache has lost some of its punctuation and
   * accents. Between a letter and a contraction, U+FFFD stands in for an
   * apostrophe ("doesn__t") and CONTRACTION repairs it. A "?" *inside* a
   * word is a lost accented letter -- "Der Sch?ler", "qualit?" -- and that
   * is not repairable, because nothing in the text says which letter it was.
   * Those reviews are still withheld: putting visibly corrupted words on the
   * page serves nobody, least of all the customer who wrote them.
   *
   * The one that was not real: language. The old test also dropped every
   * review carrying a German, French, Spanish, Italian or Dutch word, which
   * removed roughly half the corpus -- including reviews that were perfectly
   * legible ("Sehr gut", "ottimo prodotto", "Bonne qualite"). Those are back.
   * They were already being syndicated to Google by the review feed, so the
   * storefront had been the only place they were hidden.
   *
   * Measured 2026-09-13 across four products, 382 reviews: 31 carry the
   * mid-word "?" and are still held back; 85 are non-English and now show.
   */
  const CONTRACTION = /([A-Za-z])\uFFFD+(?=(?:s|t|m|d|re|ve|ll)\b)/g;
  // Two unambiguous shapes. A "?" between letters ("Sch?ler") and a "?"
  // opening a word ("?pluche" for Eplucheur) are both lost accents: real
  // question marks sit at the END of a word, never inside or in front of one.
  // Two more, added 2026-09-25 after scanning 3,554 reviews the filter let
  // through: a "?" ENDING a word with a lowercase word after it ("gro? zu",
  // "Mu? fur" -- a lost ß or u; a real question is followed by a capital), and
  // any U+FFFD left once CONTRACTION has repaired the apostrophes. cleanText
  // used to delete those silently, which printed "splmaschinengeeignet" and
  // "D'o son" -- still corrupted, just less obviously.
  const MANGLED = /[A-Za-z]\?[A-Za-z]|(?:^|\s)\?[A-Za-z]|[A-Za-z]\?\s+[a-z]|\uFFFD/;

  // Not a language test: the text is unreadable whatever language it is in.
  function isMangled(text) {
    return MANGLED.test(text.replace(CONTRACTION, "$1'"));
  }


  // Repair the apostrophes and drop the "??" that corrupted emoji leave
  // behind -- anywhere a run of them stands on its own ("Great product ????",
  // "open for bribes ??)"), not only at the end. A run attached to a word
  // ("klebrig??") is a real question and stays. Any other U+FFFD is left in
  // place for isMangled to reject rather than deleted into a misspelling.
  function cleanText(text) {
    return text
      .replace(CONTRACTION, "$1'")
      .replace(/(^|\s)\?{2,}(?=\s|$|[).,!])/g, '$1')
      .replace(/[ \t]{2,}/g, ' ')
      .replace(/\s+\)/g, ')')
      .trim();
  }

  function formatDate(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }

  function buildCard(rev) {
    const name = rev.querySelector('.jdgm-rev__author');
    const ratingEl = rev.querySelector('.jdgm-rev__rating');
    const title = rev.querySelector('.jdgm-rev__title');
    const body = rev.querySelector('.jdgm-rev__body');
    const timeEl = rev.querySelector('.jdgm-rev__timestamp');

    if (!body) return null;

    const titleText = cleanText(title ? title.textContent.trim() : '');
    const bodyText = cleanText(body.textContent.trim());
    if (!bodyText || isMangled(`${titleText} ${bodyText}`)) return null;

    const score = Math.round(parseFloat(ratingEl?.getAttribute('data-score') || '5'));

    const li = document.createElement('li');
    li.className = 'ge-pdp-reviews__card';
    li.dataset.reviewId = rev.getAttribute('data-review-id') || '';

    const top = document.createElement('div');
    top.className = 'ge-pdp-reviews__card-top';
    const nameEl = document.createElement('span');
    nameEl.className = 'ge-pdp-reviews__name';
    nameEl.textContent = name ? name.textContent.trim() : 'Customer';
    const starsEl = document.createElement('span');
    starsEl.className = 'ge-pdp-reviews__stars';
    starsEl.textContent = '★★★★★'.slice(0, Math.max(0, Math.min(5, score)));
    top.append(nameEl, starsEl);
    li.appendChild(top);

    const meta = document.createElement('div');
    meta.className = 'ge-pdp-reviews__meta';
    // Only where Judge.me itself marks the buyer verified. This used to be
    // shown on every review on the understanding that every review was from
    // a verified purchase. The provenance audit of 2026-09-03 found the
    // opposite: of 5,297 eligible reviews only 37 carry a captured IP or a
    // verified purchase, and the rest are bulk imports. Labelling those
    // "Verified" is a claim the store cannot back -- a blacklisted practice
    // for EU consumers (UCPD Annex I, 23b) and deceptive under FTC Act s5.
    if (rev.getAttribute('data-verified-buyer') === 'true') {
      const badge = document.createElement('span');
      badge.className = 'ge-pdp-reviews__verified';
      badge.textContent = '✓ Verified Customer';
      meta.appendChild(badge);
    }

    const dateText = formatDate(timeEl?.getAttribute('datetime'));
    if (dateText) {
      const dateEl = document.createElement('span');
      dateEl.className = 'ge-pdp-reviews__date';
      dateEl.textContent = dateText;
      meta.appendChild(dateEl);
    }
    if (meta.childNodes.length) li.appendChild(meta);

    // Which product the review is actually about. On a product with several
    // variants -- Meat Needle against Meat Needle Luxe -- the same review
    // reads quite differently without it. Judge.me carries it on the review
    // element, so it costs no extra request.
    const productTitle = (rev.getAttribute('data-product-title') || '').trim();
    if (productTitle) {
      const productEl = document.createElement('div');
      productEl.className = 'ge-pdp-reviews__product';
      productEl.textContent = productTitle;
      li.appendChild(productEl);
    }

    if (titleText) {
      const titleEl = document.createElement('p');
      titleEl.className = 'ge-pdp-reviews__title';
      titleEl.textContent = titleText;
      li.appendChild(titleEl);
    }

    const bodyEl = document.createElement('p');
    bodyEl.className = 'ge-pdp-reviews__body';
    bodyEl.textContent = bodyText;
    li.appendChild(bodyEl);

    return li;
  }

  function buildBar(row) {
    const stars = row.getAttribute('data-rating');

    const wrap = document.createElement('div');
    wrap.className = 'ge-pdp-reviews__bar-row';

    const label = document.createElement('span');
    label.className = 'ge-pdp-reviews__bar-label';
    label.textContent = `${stars}★`;

    const track = document.createElement('span');
    track.className = 'ge-pdp-reviews__bar-track';
    const fill = document.createElement('span');
    fill.className = 'ge-pdp-reviews__bar-fill';
    fill.style.width = `${row.getAttribute('data-percentage')}%`;
    track.appendChild(fill);

    const countEl = document.createElement('span');
    countEl.className = 'ge-pdp-reviews__bar-count';
    countEl.textContent = row.getAttribute('data-frequency');

    wrap.append(label, track, countEl);
    return wrap;
  }

  /*
   * Judge.me pages through this endpoint itself; we page through it too.
   * Parsed with DOMParser so nothing in the returned markup runs or loads --
   * only text is ever read out of it.
   */
  async function fetchPage(root, page) {
    const paginate = root.querySelector('.jdgm-paginate');
    const shop = root.dataset.shop;
    const productId = root.dataset.productId;
    if (!paginate || !shop || !productId) return null;

    const endpoint = paginate.getAttribute('data-url');
    if (!endpoint) return null;

    const query = new URLSearchParams({
      url: shop,
      shop_domain: shop,
      platform: 'shopify',
      page: String(page),
      per_page: String(FETCH_PER_PAGE),
      product_id: productId,
    });

    try {
      const response = await fetch(`${endpoint}?${query}`);
      if (!response.ok) return null;
      const payload = await response.json();
      const parsed = new DOMParser().parseFromString(payload.html || '', 'text/html');
      return [...parsed.querySelectorAll('.jdgm-rev')];
    } catch {
      return null;
    }
  }

  function init(root) {
    const source = root.querySelector('[data-ge-product-reviews-source]');
    const grid = root.querySelector('[data-ge-product-reviews-grid]');
    if (!source || !grid) return;

    const widg = source.querySelector('.jdgm-rev-widg');
    if (!widg) {
      root.style.display = 'none';
      return;
    }

    /*
     * Write a review. Judge.me's widget carries its own trigger link,
     * which its script binds a handler to and which builds, validates and
     * submits the form -- so this drives that link rather than
     * reimplementing any of it. The link ships with an inline
     * display:none, hence clicking it directly instead of revealing it.
     */
    const writeBtn = root.querySelector('[data-ge-reviews-write]');
    const writeLink = source.querySelector('.jdgm-write-rev-link');
    if (writeBtn && writeLink) {
      writeBtn.hidden = false;
      writeBtn.addEventListener('click', () => {
        const opening = !root.classList.contains('is-writing');
        root.classList.toggle('is-writing', opening);
        writeBtn.textContent = opening ? 'Cancel review' : 'Write a review';
        // The source is inert while hidden (see ge-product-reviews.liquid);
        // the form inside it has to be reachable while it is open.
        if (opening) source.inert = false;
        // Judge.me's link is the thing that builds and populates the form,
        // so it still has to be driven -- it is just never shown, because
        // one press of one button is the whole interaction.
        writeLink.click();
        if (!opening) {
          source.inert = true;
          return;
        }
        /*
         * Judge.me builds the form asynchronously and how long that takes
         * varies with connection and cache. Poll for it rather than
         * checking once: a single check is what made a slow build look
         * like a failed one.
         */
        const deadline = Date.now() + 6000;
        const settle = () => {
          const form = source.querySelector('.jdgm-form-wrapper, .jdgm-form');
          const ready = form && form.getBoundingClientRect().height > 40;
          if (ready) {
            form.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
          }
          if (Date.now() < deadline) {
            window.setTimeout(settle, 200);
            return;
          }
          // Out of time. The form is forced open by CSS regardless, so
          // scroll to wherever it is rather than leaving the visitor
          // looking at the middle of the review grid.
          (form || source).scrollIntoView({ behavior: 'smooth', block: 'center' });
        };
        window.setTimeout(settle, 200);
      });
    }

    const avgRating = parseFloat(widg.getAttribute('data-average-rating') || '0');
    const reviewCount = widg.getAttribute('data-number-of-reviews') || '0';

    const scoreEl = root.querySelector('[data-ge-product-reviews-score]');
    const starsEl = root.querySelector('[data-ge-product-reviews-stars]');
    const countEl = root.querySelector('[data-ge-product-reviews-count]');
    const barsEl = root.querySelector('[data-ge-product-reviews-bars]');

    if (avgRating > 0) {
      if (scoreEl) scoreEl.textContent = avgRating.toFixed(1);
      if (starsEl) starsEl.textContent = '\u2605\u2605\u2605\u2605\u2605'.slice(0, Math.round(avgRating));
      if (countEl) countEl.textContent = `Based on ${reviewCount} reviews`;

      document.querySelectorAll('[data-ge-pdp-rating]').forEach((el) => {
        const stars = document.createElement('span');
        stars.className = 'ge-pdp-rating__stars';
        stars.textContent = '\u2605\u2605\u2605\u2605\u2605'.slice(0, Math.round(avgRating));
        el.appendChild(stars);
        el.append(` ${avgRating.toFixed(1)} \u00b7 ${reviewCount} happy cooks`);
      });
    }

    if (barsEl) {
      source.querySelectorAll('.jdgm-histogram__row[data-rating]').forEach((row) => {
        // Judge.me closes the histogram with a clear-filter control carrying
        // data-rating="null" and no frequency. Rendering it gave every
        // product a "null star" row with a full-width bar.
        const rating = parseInt(row.getAttribute('data-rating'), 10);
        if (!(rating >= 1 && rating <= 5)) return;
        barsEl.appendChild(buildBar(row));
      });
    }

    /*
     * One page of four at a time, fetched from Judge.me a chunk at a time.
     * The language filter drops reviews, so a Judge.me page does not map to
     * a page here -- reviews are buffered and sliced instead, and the next
     * chunk is pulled only when the buffer runs short.
     */
    const cards = [];
    const seen = new Set();
    let page = 0;
    let nextFetch = 1;
    let exhausted = false;

    const collect = (reviews) => {
      let added = 0;
      reviews.forEach((rev) => {
        const id = rev.getAttribute('data-review-id') || '';
        if (id && seen.has(id)) return;
        const card = buildCard(rev);
        if (!card) return;
        if (id) seen.add(id);
        cards.push(card);
        added += 1;
      });
      return added;
    };

    collect([...source.querySelectorAll('.jdgm-rev')]);

    const pager = root.querySelector('[data-ge-product-reviews-pager]');
    const prev = root.querySelector('[data-ge-reviews-prev]');
    const next = root.querySelector('[data-ge-reviews-next]');
    const status = root.querySelector('[data-ge-reviews-status]');

    function paint() {
      grid.replaceChildren(...cards.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE));
      const from = page * PER_PAGE + 1;
      const to = Math.min(cards.length, page * PER_PAGE + PER_PAGE);
      // Total is Judge.me's own count for the product, not the number we
      // have buffered -- "1-4 of 390" reads against the same figure as
      // "Based on 390 reviews" in the summary beside it.
      if (status) status.textContent = `${from}\u2013${to} of ${reviewCount}`;
      if (prev) prev.disabled = page === 0;
      if (next) next.disabled = exhausted && (page + 1) * PER_PAGE >= cards.length;
    }

    // Keep at least one page in hand beyond the one on screen, so Next is
    // known-good before it is pressed rather than after.
    const totalReviews = parseInt(reviewCount, 10) || 0;

    /*
     * One top-up at a time. Two quick presses of Next used to start two of
     * these together: both fetched Judge.me page N (nextFetch only moves
     * after the await), then each advanced nextFetch, so it jumped to N+2 and
     * page N+1 -- 30 reviews -- was never fetched. The duplicates were
     * deduplicated, which hid it. A press that arrives mid-fetch now joins the
     * running loop, which re-reads `page` on every pass and so fetches far
     * enough for both presses.
     */
    let topping = null;
    function topUp() {
      if (!topping) {
        topping = fill().finally(() => {
          topping = null;
        });
      }
      return topping;
    }

    async function fill() {
      while (!exhausted && cards.length < (page + 2) * PER_PAGE) {
        const batch = await fetchPage(root, nextFetch);
        // An empty batch is the end. A short one is not: Judge.me does not
        // reliably fill a page, and treating short as final stranded most
        // products on their first few reviews.
        if (!batch || !batch.length) {
          exhausted = true;
          break;
        }
        nextFetch += 1;
        collect(batch);
        if (totalReviews && (nextFetch - 1) * FETCH_PER_PAGE >= totalReviews) {
          exhausted = true;
        }
      }
    }

    if (prev) {
      prev.addEventListener('click', () => {
        if (page === 0) return;
        page -= 1;
        paint();
      });
    }
    if (next) {
      next.addEventListener('click', async () => {
        if ((page + 1) * PER_PAGE >= cards.length && exhausted) return;
        page += 1;
        paint();
        await topUp();
        paint();
      });
    }

    /*
     * Nothing to list is not the same as nothing to show. This section
     * carries the Write a review button, so hiding it left a brand new
     * product with no way to collect its first review -- exactly the
     * product that most needs one.
     *
     * Judge.me renders its widget for a product with no reviews too
     * (data-number-of-reviews="0", write link present), so the button
     * still works here.
     */
    function showEmpty() {
      const empty = root.querySelector('[data-ge-product-reviews-empty]');
      root.classList.add('is-empty');
      if (empty) {
        /*
         * Two different situations, and saying "no reviews yet" for the
         * second would be untrue: the reviews exist, they are just not in
         * English, and the summary beside this still shows their rating.
         */
        empty.textContent =
          totalReviews > 0
            ? 'No written reviews to show here yet.'
            : 'No reviews yet — be the first to tell other cooks what you think.';
        empty.hidden = false;
      }
      if (totalReviews === 0) root.classList.add('is-unreviewed');
    }

    function show() {
      if (pager) pager.hidden = false;
      paint();
      topUp().then(paint);
    }

    /*
     * An empty first page is not an empty product. The inline Judge.me widget
     * only carries its first five reviews, and a review with a rating but no
     * written body yields no card, so a product whose first five are all
     * ratings-only would fall straight to "No written reviews to show here
     * yet" -- with no pager -- while written ones sat on page 2. Pull further
     * pages before believing it.
     *
     * This mattered more when a language filter was also dropping about half
     * the corpus; that is gone, but the guard is still right on its own
     * terms.
     */
    if (!cards.length) {
      if (totalReviews > 0 && !exhausted) {
        topUp().then(() => (cards.length ? show() : showEmpty()));
        return;
      }
      showEmpty();
      return;
    }

    show();
  }

  function initAll() {
    document.querySelectorAll('[data-ge-product-reviews]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
